"use client";

import { createContext, useContext, useEffect, useState } from "react";

export interface SiteSettings {
  branding: {
    favicon: string;
    logoLight: string;
    logoDark: string;
    mobileLogoLight: string;
    mobileLogoDark: string;
    footerLogo: string;
    textLogo: string;
    displayMode: "logo" | "text" | "both";
  };
  general: {
    siteName: string;
    organizationName: string;
    defaultMetaTitle: string;
    defaultMetaDescription: string;
    contactEmail: string;
    contactPhone: string;
    footerDescription: string;
    companyDescription: string;
    copyrightText: string;
    socialLinks: Array<{ name: string; href: string; icon: string }>;
  };
  scripts: {
    customJs: string;
    analyticsCode: string;
  };
  theme: {
    defaultTheme: "light" | "dark" | "system";
    colorPresets: Record<string, string>;
  };
  captcha: {
    enabled: boolean;
    provider: "google_recaptcha_v3";
    siteKey: string;
    minimumScore: number;
  };
  notifications: {
    footerCta: {
      enabled: boolean;
      badge: string;
      title: string;
      description: string;
      primaryLabel: string;
      primaryHref: string;
      secondaryLabel: string;
      secondaryHref: string;
    };
  };
  maintenance: {
    enabled: boolean;
    title: string;
    message: string;
    contactTitle: string;
    contactDescription: string;
    contactEmail: string;
    contactPhone: string;
  };
  typography?: {
    headingFont?: string;
    headingFontFamily?: string;
    headingStyle?: string;
    headingGradientColors?: string[];
  };
}

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  branding: {
    favicon: "",
    logoLight: "",
    logoDark: "",
    mobileLogoLight: "",
    mobileLogoDark: "",
    footerLogo: "",
    textLogo: "NotifyStudio",
    displayMode: "both",
  },
  general: {
    siteName: "NotifyStudio",
    organizationName: "NotifyStudio",
    defaultMetaTitle: "NotifyStudio | Website Engagement and Notification Platform",
    defaultMetaDescription:
      "NotifyStudio helps businesses improve engagement and conversions with social proof, popups, and lead generation widgets.",
    contactEmail: "hello@notifystudio.com",
    contactPhone: "",
    footerDescription:
      "Smart website engagement widgets for lead generation, social proof, and conversion growth.",
    companyDescription:
      "NotifyStudio is a SaaS platform for website notifications, conversion tools, and engagement automation.",
    copyrightText: "NotifyStudio. All rights reserved.",
    socialLinks: [],
  },
  scripts: {
    customJs: "",
    analyticsCode: "",
  },
  theme: {
    defaultTheme: "system",
    colorPresets: {},
  },
  captcha: {
    enabled: false,
    provider: "google_recaptcha_v3",
    siteKey: "",
    minimumScore: 0.5,
  },
  notifications: {
    footerCta: {
      enabled: false,
      badge: "Boost Engagement",
      title: "Need help optimizing your website conversion journey?",
      description: "Talk to NotifyStudio about notification strategy, popup optimization, and funnel improvements tailored to your audience.",
      primaryLabel: "Book a Demo",
      primaryHref: "/contact",
      secondaryLabel: "Explore Features",
      secondaryHref: "/features",
    },
  },
  maintenance: {
    enabled: false,
    title: "Under Maintenance",
    message: "We are currently performing scheduled maintenance. Please check back shortly.",
    contactTitle: "Need immediate assistance?",
    contactDescription: "If your request is urgent, reach out to our team using the contact details below.",
    contactEmail: "",
    contactPhone: "",
  },
  typography: {
    headingFont: "",
    headingFontFamily: "",
    headingStyle: "rainbow",
    headingGradientColors: ["#ff6b6b", "#feca57", "#48dbfb", "#ff9ff3", "#54a0ff", "#5f27cd", "#ff6b6b"],
  },
};

type SiteSettingsStatus = "loading" | "ready" | "offline";

interface SiteSettingsContextValue {
  settings: SiteSettings;
  status: SiteSettingsStatus;
  refresh: () => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue>({
  settings: DEFAULT_SITE_SETTINGS,
  status: "loading",
  refresh: async () => undefined,
});

const SETTINGS_STORAGE_KEY = "notifystudio-site-settings-cache";

let cachedSettings: SiteSettings | null = null;
let fetchPromise: Promise<{ settings: SiteSettings; status: SiteSettingsStatus }> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60_000;
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://admin.notifystudio.com/api/public";

function mergeSettings(data: Partial<SiteSettings>) {
  return {
    ...DEFAULT_SITE_SETTINGS,
    ...data,
    branding: { ...DEFAULT_SITE_SETTINGS.branding, ...data.branding },
    general: { ...DEFAULT_SITE_SETTINGS.general, ...data.general },
    scripts: { ...DEFAULT_SITE_SETTINGS.scripts, ...data.scripts },
    theme: { ...DEFAULT_SITE_SETTINGS.theme, ...data.theme },
    notifications: { ...DEFAULT_SITE_SETTINGS.notifications, ...data.notifications },
    maintenance: { ...DEFAULT_SITE_SETTINGS.maintenance, ...data.maintenance },
      typography: { ...(DEFAULT_SITE_SETTINGS as any).typography, ...(data as any).typography },
  };
}

function readStoredSettings() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return mergeSettings(JSON.parse(raw) as Partial<SiteSettings>);
  } catch {
    return null;
  }
}

function persistSettings(settings: SiteSettings) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
  }
}

async function fetchSettings(bustCache = false) {
  const now = Date.now();
  if (!bustCache && cachedSettings && now - cacheTimestamp < CACHE_TTL) {
    return { settings: cachedSettings, status: "ready" as const };
  }
  if (!bustCache && fetchPromise) return fetchPromise;

  fetchPromise = fetch(`${API_BASE}/settings`, { cache: "no-store" })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to fetch site settings.");
      }

      return response.json();
    })
    .then((data: Partial<SiteSettings>) => {
      cachedSettings = mergeSettings(data);
      cacheTimestamp = Date.now();
      persistSettings(cachedSettings);
      fetchPromise = null;
      return { settings: cachedSettings, status: "ready" as const };
    })
    .catch(() => {
      fetchPromise = null;
      const fallback = readStoredSettings() || cachedSettings || DEFAULT_SITE_SETTINGS;
      cachedSettings = fallback;
      return { settings: fallback, status: "offline" as const };
    });

  return fetchPromise;
}

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [status, setStatus] = useState<SiteSettingsStatus>("loading");

  const refresh = async () => {
    const next = await fetchSettings(true);
    setSettings(next.settings);
    setStatus(next.status);
  };

  useEffect(() => {
    const stored = readStoredSettings();
    if (stored) {
      setSettings(stored);
      setStatus("ready");
    }

    void fetchSettings().then((next) => {
      setSettings(next.settings);
      setStatus(next.status);
    });

    const handleFocus = () => {
      void refresh();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  return <SiteSettingsContext.Provider value={{ settings, status, refresh }}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
